import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import { tap, filter, take, switchMap } from 'rxjs/operators';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ExternalProvider,
  ConfirmEmailRequest,
  ConfirmEmailSetPasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  SendTwoFactorCodeRequest,
  VerifyTwoFactorCodeRequest,
  UserInterest,
  UpdateProfileRequest,
} from '../../models/auth.model';
import { UserStateService } from './user-state.service';
import { UserStateResponse } from '../../models/user-state.model';

import { logger } from '../../helpers/logger';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private currentUser$ = new BehaviorSubject<any>(null);
  private authInitialized$ = new ReplaySubject<boolean>(1);

  public readonly isAuthenticated = this.isAuthenticated$.asObservable();
  public readonly currentUser = this.currentUser$.asObservable();
  public readonly authInitialized = this.authInitialized$.asObservable();

  constructor(
    private http: HttpClient,
    private injector: Injector
  ) {
    this.checkAuthenticationStatus();
  }

  login(request: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/login', request).pipe(
      tap(response => {
        if (response.success) {
          this.isAuthenticated$.next(true);
          this.loadCurrentUser();
        }
      })
    );
  }

  register(request: RegisterRequest): Observable<AuthResponse> {
    // Note: registration no longer signs the user in — email confirmation is required first.
    // Do not update authentication state here.
    return this.http.post<AuthResponse>('/api/account/register', request);
  }

  getExternalProviders(): Observable<ExternalProvider[]> {
    return this.http.get<ExternalProvider[]>('/api/account/external-providers');
  }

  getUserInterests(): Observable<UserInterest[]> {
    return this.http.get<UserInterest[]>('/api/account/user-interests');
  }

  logout(): Observable<any> {
    return this.http.post('/api/account/logout', {}).pipe(
      tap(() => {
        this.isAuthenticated$.next(false);
        this.currentUser$.next(null);
      })
    );
  }

  confirmEmail(request: ConfirmEmailRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/confirm-email', request);
  }

  confirmEmailSetPassword(request: ConfirmEmailSetPasswordRequest): Observable<any> {
    return this.http.post<any>('/api/account/confirm-email-set-password', request);
  }

  forgotPassword(request: ForgotPasswordRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/forgot-password', request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/reset-password', request);
  }

  resendConfirmationEmail(email: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/resend-confirmation-email', { email });
  }

  sendTwoFactorCode(request: SendTwoFactorCodeRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/send-2fa-code', request);
  }

  verifyTwoFactorCode(request: VerifyTwoFactorCodeRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/verify-2fa-code', request).pipe(
      tap(response => {
        if (response.success) {
          this.isAuthenticated$.next(true);
          this.loadCurrentUser();
        }
      })
    );
  }

  updateProfile(request: UpdateProfileRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/account/update-profile', request).pipe(
      tap(response => {
        if (response.success) {
          this.loadCurrentUser();
        }
      })
    );
  }

  getProfile(): Observable<any> {
    return this.http.get<any>('/api/account/profile');
  }

  getUserState(): Observable<UserStateResponse> {
    return this.http.get<UserStateResponse>('/api/account/user-state');
  }

  private checkAuthenticationStatus(): void {
    // Use the centralized UserStateService instead of making direct API calls
    const userStateService = this.injector.get(UserStateService);

    userStateService.getInitializedUserState().subscribe({
      next: (userState: UserStateResponse) => {
        this.isAuthenticated$.next(userState.isAuthenticated);
        this.currentUser$.next(userState.user);
        this.authInitialized$.next(true);
      },
      error: (error: any) => {
        logger.error('Authentication status check failed:', error);
        this.isAuthenticated$.next(false);
        this.currentUser$.next(null);
        this.authInitialized$.next(true);
      },
    });
  }

  /**
   * Returns an observable that emits the authentication status only after initialization is complete
   */
  public getAuthenticationStatus(): Observable<boolean> {
    return this.authInitialized$.pipe(
      filter(initialized => initialized),
      take(1),
      tap(() => {}), // Ensure the auth check has completed
      switchMap(() => this.isAuthenticated$),
      take(1)
    );
  }
  private loadCurrentUser(): void {
    // Use the centralized UserStateService to refresh state
    const userStateService = this.injector.get(UserStateService);
    userStateService.refreshUserState();

    // Subscribe to the updated state
    userStateService.userState$.pipe(take(1)).subscribe({
      next: (userState: UserStateResponse | null) => {
        if (userState) {
          this.isAuthenticated$.next(userState.isAuthenticated);
          this.currentUser$.next(userState.user);
        }
      },
      error: (error: any) => {
        logger.error('Failed to load current user:', error);
        this.isAuthenticated$.next(false);
        this.currentUser$.next(null);
      },
    });
  }
}
